import { CollapsibleContainer } from '@/features/Application/elements'
import {
  Link,
  Paragraph,
  Section,
  Subheader,
  TrustScoreTable,
  TrustScoreRow,
  ColumnHeader,
  RowHeader,
  TableText,
  MobileRowWrapper,
  MobileRow,
  MobileTableWrapper
} from './modal-elements'

export const getModalInfo = (key, t) => {
  switch (key) {
    case 'credit-summary':
      const creditSummarySubHeaders = [
        {
          title: t('l.application.view.revolving.subtitle', 'Revolving'),
          description: t(
            'l.application.view.revolving_description.text',
            'Revolving Credit (e.g. credit cards, lines of credit) refers to money that is borrowed, paid back, then borrowed again. This type of credit typically comes with higher interest rates. '
          )
        },
        {
          title: t('l.application.view.instalment.subtitle', 'Instalment'),
          description: t(
            'l.application.view.instalment_description.text',
            'Instalment Credit (e.g. auto loans, personal loans) refers to a fixed sum of money that is paid back in periodic instalments over a set timeframe.'
          )
        },
        {
          title: t('l.application.view.mortgage.subtitle', 'Mortgage'),
          description: t(
            'l.application.view.mortgage_description.text',
            'Mortgages refer to loans often used to purchase a home or other property. These are generally large loans that are paid off over many years.'
          )
        },
        {
          title: t('l.application.view.total_loans.subtitle', 'Total Amount of Loan(s)'),
          description: t(
            'l.application.view.total_loans_description.text',
            'The Total Amount of Loan(s) shows the aggregate amount of all Revolving, Instalment, and Mortgage loans to the applicant.'
          )
        },
        {
          title: t('l.application.view.total_balance.subtitle', 'Total Balance Remaining'),
          description: t(
            'l.application.view.total_balance_description.text',
            'The Total Balance Remaining refers to the proportion of the borrowed amount still owed by the applicant.'
          )
        },
        {
          title: t('l.application.view.total_monthly.subtitle', 'Total Monthly Payment'),
          description: t(
            'l.application.view.total_monthly_description.text',
            'Total Monthly Pay[ment] shows the aggregate amount owed by the applicant every month for each loan type.'
          )
        },
        {
          title: t('l.application.view.total_credit_limit.subtitle', 'Total Credit Limit'),
          description: t(
            'l.application.view.total_credit_limit.text',
            'Total Credit Limit displays the total amount of credit available to the applicant for each loan type, including any amount already borrowed.'
          )
        },
        {
          title: t('l.application.view.late_payments.subtitle', 'Existing Late Payments'),
          description: t(
            'l.application.view.existing_late_payments.text',
            'Existing Late Payments shows any repayments of the loan which were made after their due date.'
          )
        },
        {
          title: t('l.application.view.average_days_to_pay.subtitle', 'Average Days to Pay'),
          description: t(
            'l.application.view.average_days_to_pay.text',
            'Average Days to Pay measures the average number of days it takes for the applicant to make a payment after receiving a bill or invoice. Please note, any significant late payments or severe arrangements will default to 150 - 200 days.'
          )
        }
      ]
      return {
        title: t('l.application.view.credit_summary.title', 'Credit Summary'),
        isPoweredByEquifax: true,
        content: (
          <>
            <Paragraph marginBottom="24px">
              {t(
                'l.application.view.credit_summary_description.text',
                "A renter's Credit Summary is an extensive analysis of their credit history from the last 4 years on all accounts. This section flags any payments past due that may be indicative of higher debt levels."
              )}
            </Paragraph>
            {creditSummarySubHeaders.map((subheader, index) => (
              <Section key={`${subheader}-${index}`}>
                <Subheader>{subheader.title}</Subheader>
                <Paragraph padding="12px">{subheader.description}</Paragraph>
              </Section>
            ))}
          </>
        )
      }

    case 'risk-assessment':
      const riskAssessmentSubHeaders = [
        {
          title: t(
            'l.application.view.court_record_verification.subtitle',
            'Court Record Verification'
          ),
          description: t(
            'l.application.view.court_record_verification_description.text',
            'Court Record Verification displays any records of possible infractions associated with the applicant. Information is gathered from publicly available provincial/state court records.'
          )
        },
        {
          title: t('l.application.view.potential_risks.subtitle', 'Potential Risks'),
          description: t(
            'l.application.view.potential_risks_description.text',
            "Potential Risks displays any risk factors associated with this applicant's reliability and ability to pay rent on time, based on any anomalies that were found in their financial history."
          )
        },
        {
          title: t('l.application.view.bankruptcies.subtitle', 'Bankruptcies'),
          description: t(
            'l.application.view.bankruptcies_description.text',
            'Bankruptcy Index shows any bankruptcy records available for the renter from within the past 6 years. This is intended to predict the likelihood of a renter filing for bankruptcy in the immediate 12-24 months.'
          )
        },
        {
          title: t('l.application.view.collections_record.subtitle', 'Collections Record'),
          description: t(
            'l.application.view.collections_record_description.text',
            "The Collections Record displays the number of a renter's accounts that have been assigned to a Collections Agency, if any exist. Generally, this information will remain on a renter's Equifax®️ credit checks for 6 years following the date of the final payment. "
          )
        }
      ]
      return {
        title: t('l.application.view.risk_assessment.title', 'Risk Assessment'),
        isPoweredByEquifax: true,
        content: (
          <>
            <Paragraph marginBottom="24px">
              {t(
                'l.application.view.risk_assessment_description.text',
                "An applicant's Risk Assessment looks at factors that could affect their ability to make rent payments on time and other markers that indicate poor financial standing."
              )}
            </Paragraph>
            {riskAssessmentSubHeaders.map((subheader, index) => (
              <Section key={`${subheader}-${index}`}>
                <Subheader>{subheader.title}</Subheader>
                <Paragraph padding="12px">{subheader.description}</Paragraph>
              </Section>
            ))}
          </>
        )
      }

    case 'trustworthiness':
      const trustworthinessSubHeaders = [
        {
          title: t('l.application.view.identity_verification.subtitle', 'Identity Verification'),
          description: t(
            'l.application.view.identity_verification_description.text',
            'Identity Verification requires renters to complete a two-step process where renters must upload a valid, government-issued photo ID and match it with a real-time selfie to corroborate their identity.'
          )
        },
        {
          title: t('l.application.view.social_verification.subtitle', 'Social Verification'),
          description: t(
            'l.application.view.social_verification_description.text',
            "Our Social Verification process employs an advanced algorithm that automatically cross-references the applicant's profile with their connected LinkedIn account to confirm a match."
          )
        },
        {
          title: t(
            'l.application.view.employment_verification.subtitle',
            'Employment Verification'
          ),
          description: t(
            'l.application.view.employment_verification_description.text',
            "Employment Verification confirms the renter's stated employment by matching documents they submit (e.g. T4, business card, job offer letter, or pay stub) to the information in their profile."
          )
        },
        {
          title: t('l.application.view.income_verification.subtitle', 'Income Verification'),
          description: t(
            'l.application.view.income_verification_description.text',
            "Income Verification checks an applicant's supporting documents (e.g. T4, pay slip, bank statement) against the financial details entered within their profile in order to confirm a match."
          )
        }
      ]
      return {
        title: t('l.application.view.trustworthiness.title', 'Trustworthiness'),
        isPoweredByEquifax: false,
        content: (
          <>
            <Paragraph marginBottom="24px">
              {t(
                'l.application.view.trustworthiness_description.text',
                "An applicant's Trustworthiness is calculated using a proprietary algorithm that measures the tenant's reliability through a multi-faceted verification process that includes social media, employment, income, and identity verification."
              )}
            </Paragraph>
            {trustworthinessSubHeaders.map((subheader, index) => (
              <Section key={`${subheader}-${index}`}>
                <Subheader>{subheader.title}</Subheader>
                <Paragraph padding="12px">{subheader.description}</Paragraph>
              </Section>
            ))}
          </>
        )
      }

    case 'rent-to-income':
      return {
        title: t('l.application.view.rent_to_income.title', 'Rent-to-Income'),
        isPoweredByEquifax: false,
        content: (
          <>
            <Paragraph marginBottom={'24px'}>
              {t(
                'l.application.view.rent_to_income_description_1.text',
                "An applicant's Rent-to-Income Ratio is calculated as a percentage based on their gross monthly income, divided by the amount of their rent payments. The recommended Rent-to-Income in Canada is 30%."
              )}
            </Paragraph>
            <Paragraph marginBottom={'24px'}>
              {t(
                'l.application.view.rent_to_income_description_2.text',
                "To calculate this score, we take the highest number of the renter's self-declared salary range. For example, if a renter declared a yearly salary of $100,000 – 150,000 their rent-to-income ratio would be based on a gross annual income of $150,000. "
              )}
            </Paragraph>
            <Link
              href="https://www.cmhc-schl.gc.ca/en/professionals/industry-innovation-and-leadership/industry-expertise/affordable-housing/about-affordable-housing/affordable-housing-in-canada"
              rel="noopener noreferrer"
              target="_blank"
            >
              {t('l.application.view.learn_more.button', 'Learn more')}
            </Link>
          </>
        )
      }

    case 'trust-score':
      return {
        title: t('l.application.view.trust_score.title', 'Trust Score'),
        isPoweredByEquifax: false,
        content: (
          <>
            <Paragraph marginBottom={'24px'}>
              {t(
                'l.application.view.trust_score_description.text',
                "The Trust Score is a comprehensive metric designed to evaluate prospective tenants' suitability by analyzing key information like Equifax® credit checks, risk assessment, income & employment verifications, court records, and more."
              )}
            </Paragraph>
            <TrustScoreTable>
              <TrustScoreRow isHeader>
                <ColumnHeader>
                  {t('l.application.view.trust_score_rating.title', 'Rating')}
                </ColumnHeader>
                <ColumnHeader>
                  {t('l.application.view.trust_score_trust_score.title', 'Trust Score')}
                </ColumnHeader>
                <ColumnHeader>
                  {t(
                    'l.application.view.trust_score_other_credit.title',
                    'Other Credit Rating Score'
                  )}
                </ColumnHeader>
                <ColumnHeader>
                  {t('l.application.view.trust_score_Explanation.title', 'Explanation')}
                </ColumnHeader>
              </TrustScoreRow>
              <TrustScoreRow>
                <RowHeader>
                  {t('l.application.view.trust_score_excellent.title', 'Excellent')}
                </RowHeader>
                <TableText>90+</TableText>
                <TableText>800+</TableText>
                <TableText>
                  {t(
                    'l.application.view.trust_score_excellent.text',
                    'This applicant has a strong financial history and very few or no missed payments. They should make a great tenant!'
                  )}
                </TableText>
              </TrustScoreRow>
              <TrustScoreRow isGrey>
                <RowHeader>
                  {t('l.application.view.trust_score_very_good.title', 'Very Good')}
                </RowHeader>
                <TableText>75-89</TableText>
                <TableText>700's</TableText>
                <TableText>
                  {t(
                    'l.application.view.trust_score_very_good.text',
                    'You should have very little to worry about with this applicant when it comes to receiving rent payments on time.'
                  )}
                </TableText>
              </TrustScoreRow>
              <TrustScoreRow>
                <RowHeader>
                  {t('l.application.view.trust_score_average.title', 'Average')}
                </RowHeader>
                <TableText>60-74</TableText>
                <TableText>600's</TableText>
                <TableText>
                  {t(
                    'l.application.view.trust_score_average.text',
                    'This applicant has a fair financial history, though they might have missed payments in the past. You may want to request additional documents that prove their financial suitability.'
                  )}
                </TableText>
              </TrustScoreRow>
              <TrustScoreRow isGrey>
                <RowHeader>{t('l.application.view.trust_score_poor.title', 'Poor')}</RowHeader>
                <TableText>0-59</TableText>
                <TableText>{'<600'}</TableText>
                <TableText>
                  {t(
                    'l.application.view.trust_score_poor.text',
                    'This applicant may have a history of not paying bills on time, or they may not have a credit history in Canada yet. We recommend requesting additional documents to clarify their financial situation.'
                  )}
                </TableText>
              </TrustScoreRow>
            </TrustScoreTable>
            <Link
              href="https://liv.rent/blog/2019/10/trust-score-explained/"
              rel="noopener noreferrer"
              target="_blank"
            >
              {t('l.application.view.learn_more.button', 'Learn more')}
            </Link>
          </>
        ),
        mobileContent: (
          <>
            <MobileTableWrapper>
              <CollapsibleContainer
                title={t('l.application.view.mobile_excellent_header.title', 'Rating: Excellent')}
                defaultOpen={true}
              >
                <MobileRowWrapper>
                  <MobileRow>
                    <RowHeader>
                      {t('l.application.view.trust_score_trust_score.title', 'Trust Score')}
                    </RowHeader>
                    <TableText>90+</TableText>
                  </MobileRow>
                </MobileRowWrapper>
                <MobileRowWrapper>
                  <MobileRow>
                    <RowHeader>
                      {t(
                        'l.application.view.trust_score_other_credit.title',
                        'Other Credit Rating Score'
                      )}
                    </RowHeader>
                    <TableText>800+</TableText>
                  </MobileRow>
                </MobileRowWrapper>
                <MobileRowWrapper>
                  <MobileRow>
                    <RowHeader>
                      {t('l.application.view.trust_score_Explanation.title', 'Explanation')}
                    </RowHeader>
                    <TableText>
                      {t(
                        'l.application.view.trust_score_excellent.text',
                        'This applicant has a strong financial history and very few or no missed payments. They should make a great tenant!'
                      )}
                    </TableText>
                  </MobileRow>
                </MobileRowWrapper>
              </CollapsibleContainer>
              <CollapsibleContainer
                title={t('l.application.view.mobile_very_good_header.title', 'Rating: Very Good')}
                defaultOpen={false}
              >
                <MobileRowWrapper>
                  <MobileRow>
                    <RowHeader>
                      {t('l.application.view.trust_score_trust_score.title', 'Trust Score')}
                    </RowHeader>
                    <TableText>75-89</TableText>
                  </MobileRow>
                </MobileRowWrapper>
                <MobileRowWrapper>
                  <MobileRow>
                    <RowHeader>
                      {t(
                        'l.application.view.trust_score_other_credit.title',
                        'Other Credit Rating Score'
                      )}
                    </RowHeader>
                    <TableText>700s</TableText>
                  </MobileRow>
                </MobileRowWrapper>
                <MobileRowWrapper>
                  <MobileRow>
                    <RowHeader>
                      {t('l.application.view.trust_score_Explanation.title', 'Explanation')}
                    </RowHeader>
                    <TableText>
                      {t(
                        'l.application.view.trust_score_very_good.text',
                        'You should have very little to worry about with this applicant when it comes to receiving rent payments on time.'
                      )}
                    </TableText>
                  </MobileRow>
                </MobileRowWrapper>
              </CollapsibleContainer>
              <CollapsibleContainer
                title={t('l.application.view.mobile_average_header.title', 'Rating: Average')}
                defaultOpen={false}
              >
                <MobileRowWrapper>
                  <MobileRow>
                    <RowHeader>
                      {t('l.application.view.trust_score_trust_score.title', 'Trust Score')}
                    </RowHeader>
                    <TableText>60-74</TableText>
                  </MobileRow>
                </MobileRowWrapper>
                <MobileRowWrapper>
                  <MobileRow>
                    <RowHeader>
                      {t(
                        'l.application.view.trust_score_other_credit.title',
                        'Other Credit Rating Score'
                      )}
                    </RowHeader>
                    <TableText>600s</TableText>
                  </MobileRow>
                </MobileRowWrapper>
                <MobileRowWrapper>
                  <MobileRow>
                    <RowHeader>
                      {t('l.application.view.trust_score_Explanation.title', 'Explanation')}
                    </RowHeader>
                    <TableText>
                      {t(
                        'l.application.view.trust_score_average.text',
                        'This applicant has a fair financial history, though they might have missed payments in the past. You may want to request additional documents that prove their financial suitability.'
                      )}
                    </TableText>
                  </MobileRow>
                </MobileRowWrapper>
              </CollapsibleContainer>
              <CollapsibleContainer
                title={t('l.application.view.mobile_poor_header.title', 'Rating: Poor')}
                defaultOpen={false}
              >
                <MobileRowWrapper>
                  <MobileRow>
                    <RowHeader>
                      {t('l.application.view.trust_score_trust_score.title', 'Trust Score')}
                    </RowHeader>
                    <TableText>0-59</TableText>
                  </MobileRow>
                </MobileRowWrapper>
                <MobileRowWrapper>
                  <MobileRow>
                    <RowHeader>
                      {t(
                        'l.application.view.trust_score_other_credit.title',
                        'Other Credit Rating Score'
                      )}
                    </RowHeader>
                    <TableText>{'<600'}</TableText>
                  </MobileRow>
                </MobileRowWrapper>
                <MobileRowWrapper>
                  <MobileRow>
                    <RowHeader>
                      {t('l.application.view.trust_score_Explanation.title', 'Explanation')}
                    </RowHeader>
                    <TableText>
                      {t(
                        'l.application.view.trust_score_poor.text',
                        'This applicant may have a history of not paying bills on time, or they may not have a credit history in Canada yet. We recommend requesting additional documents to clarify their financial situation.'
                      )}
                    </TableText>
                  </MobileRow>
                </MobileRowWrapper>
              </CollapsibleContainer>
            </MobileTableWrapper>
            <Link
              href="https://liv.rent/blog/2019/10/trust-score-explained/"
              rel="noopener noreferrer"
              target="_blank"
            >
              {t('l.application.view.learn_more.button', 'Learn more')}
            </Link>
          </>
        )
      }

    default:
      break
  }
}
